import TileLayer from '@arcgis/core/layers/TileLayer';
import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { AlarmsServiceInterface, FsAlarmAdvancedMarker, PopupRequestEvent } from '@lib/alarms';
import { SnAlarm, SnCamera, SnUserSettings } from '@lib/api/entities';
import { FsMapAlarmsServiceProvider, IFsMap } from '@lib/geo/core';
import { GoogleMapPopup } from '@lib/geo/google/map';
import { FsMapPopup, MapCamerasFacadeService } from '@lib/geo/map';
import { SnMapClusterPoint } from 'libs/api/src/lib/interfaces/map-response.interface';
import { GoogleMapLayer } from 'libs/geo/google/map/src/lib/overlays';

@Injectable()
export class MapAlarmsFacadeService<TMap extends IFsMap> extends FsMapAlarmsServiceProvider<TMap> {
  constructor(
    private readonly _provider: FsMapAlarmsServiceProvider<TMap>,
    private readonly _alarmsService: AlarmsServiceInterface,
  ) {
    super();
  }

  get geospatialProvider(): string {
    return this._provider.geospatialProvider;
  }

  get id(): string {
    return this._provider.id;
  }

  get selected(): SnAlarm {
    return this._alarmsService?.selectedAlarm || null;
  }

  get selectedAlarm$(): Observable<{ alarm: SnAlarm; cameras: SnCamera[]; action?: string }> {
    return this._provider.selectedAlarm$;
  }

  get isAvailable(): boolean {
    return this._provider.isAvailable;
  }

  get viewAlarmDetails$(): Observable<SnAlarm> {
    return this._alarmsService?.viewAlarmDetails$ || this._provider.viewAlarmDetails$;
  }

  get requestPopupOpen$(): Observable<PopupRequestEvent> {
    return this._alarmsService?.requestPopupOpen$;
  }

  get requestNavigation$(): Observable<{ alarm: SnAlarm; coordinates?: [number, number] }> {
    return this._alarmsService?.requestNavigation$;
  }

  get selectedAlarmFromBusiness$(): Observable<{
    alarm: SnAlarm;
    cameras: SnCamera[];
    action?: string;
  }> {
    return this._alarmsService?.selectedAlarm$;
  }

  get mapLayer(): GoogleMapLayer | TileLayer {
    return this._provider.mapLayer;
  }

  get map(): IFsMap {
    return this._provider.map;
  }

  get selectedPopup(): GoogleMapPopup | FsMapPopup<TMap> {
    return this._provider.selectedPopup;
  }

  get isSlewing(): boolean {
    return this._alarmsService?.isSlewing || false;
  }

  set isSlewing(value: boolean) {
    if (this._alarmsService) {
      this._alarmsService.setIsSlewing(value);
    }
  }

  get alertClustering(): boolean {
    return this._alarmsService?.alertClustering || false;
  }

  set alertClustering(value: boolean) {
    this._alarmsService?.setAlertClustering(value);
  }

  get alarmDialogs(): MatDialogRef<unknown, unknown>[] {
    return this._alarmsService?.alarmDialogs || [];
  }

  get results(): SnAlarm[] {
    return this._alarmsService?.alarms || [];
  }

  get clusterResults(): SnMapClusterPoint[] {
    return this._alarmsService?.clusteredAlarms || [];
  }

  get alarmAdvancedMarker(): FsAlarmAdvancedMarker[] {
    return this._provider.alarmAdvancedMarker;
  }

  get alarmClusterAdvancedMarkers(): FsAlarmAdvancedMarker[] {
    return this._provider.alarmClusterAdvancedMarkers;
  }

  get isToggled(): boolean {
    return this._alarmsService?.isToggled || false;
  }

  get settings(): SnUserSettings {
    return this._alarmsService?.settingsValue || null;
  }

  public onMapLoaded(
    map: TMap,
    filteredTypes: string[] = [],
    filteredReasons: string[] = [],
  ): void {
    this._provider.onMapLoaded(map, filteredTypes, filteredReasons);
  }

  public isSelected(id: string): boolean {
    return this._alarmsService?.isSelected(id) || false;
  }

  public select(id: string, action?: string): void {
    if (this._alarmsService) {
      const cameraLayerObjects = this._provider['_camerasService']?.cameraLayerObjects || [];
      this._alarmsService.select(id, action, cameraLayerObjects).subscribe();
    }
  }

  public viewAlarmDetails(id: string): void {
    this._alarmsService.viewAlarmDetails(id);
  }

  public deselect(): void {
    this._provider.deselect();
  }

  public addToMap(): void {
    this._alarmsService.setIsToggled(true);
    this._provider.addToMap();
  }

  public removeFromMap(): void {
    this._alarmsService.setIsToggled(false);
    this._provider.removeFromMap();
  }

  public setAlertClustering(enabled: boolean): void {
    this._alarmsService.setAlertClustering(enabled);
    this._provider.setAlertClustering(enabled);
  }

  public closeAlarmPopupOnListener(alarm: SnAlarm): void {
    this._provider.closeAlarmPopupOnListener(alarm);
  }

  public setCamerasService(camerasService: MapCamerasFacadeService<TMap> | null): void {
    this._provider.setCamerasService(camerasService);
  }

  public isSkydioAlarm(alarm: SnAlarm): boolean {
    return this._provider.isSkydioAlarm(alarm);
  }

  public getCurrentLocationForAlarm(alarm: SnAlarm): [number, number] | null {
    return this._provider.getCurrentLocationForAlarm(alarm);
  }
}
